// Rug Room Shots: "Jobs" tab. All jobs with progress, review counts and money, the
// speed of each job and its half-price batches at Google (SPEC 9.11);
// actions Open plan / Review / Generate remaining (Fast or Half price) / Cancel / Export CSV.
// Polls GET /jobs every 10 s while this tab is mounted (SPEC 5.2).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, FormControlLabel, Switch, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TablePagination, Button, Tooltip, IconButton, Alert, CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  MdRefresh, MdListAlt, MdRateReview, MdPlayArrow, MdCancel, MdFileDownload,
} from 'react-icons/md';
import rrsApi from './api';
import {
  AI_COLOR, JOB_STATUS, fmtUsd, fmtInt, fmtAgo, tsMs, errText, usePolling, useNow, NyTime, StatusChip, ConfirmDialog, CostText,
  isDailyLimit, dailyLimitText, overBudget, overBudgetText, signalSpend, serverText,
} from './common';
import {
  SpeedChip, SpeedChooser, speedsFromConfig, rateFor, estimateFor, defaultSpeed, speedLabel, isBatch, batchUnavailableFromError,
  OVERDUE_MS,
} from './speed';

const PAGE_SIZE = 25;

const Count = ({ n, color, title }) => {
  const el = (
    <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: n ? color : 'text.disabled', fontWeight: n ? 600 : 400 }}>
      {fmtInt(n || 0)}
    </Typography>
  );
  return title ? <Tooltip title={title}>{el}</Tooltip> : el;
};

// AI check of the job's current images (SPEC_AICHECK 4.4 / 5.11): pass / needs a look / fail, and under them what is
// not checked, being checked or could not be checked. "-" when the job has no finished image.
const AiCounts = ({ c }) => {
  const n = (k) => Number(c && c[k]) || 0;
  const total = n('pass') + n('look') + n('fail') + n('unchecked') + n('checking') + n('error');
  if (!total) return <Typography variant="body2" color="text.disabled">-</Typography>;
  const extra = [
    n('unchecked') ? `${fmtInt(n('unchecked'))} not checked` : null,
    n('checking') ? `${fmtInt(n('checking'))} checking` : null,
    n('error') ? `${fmtInt(n('error'))} could not be checked` : null,
  ].filter(Boolean).join(' · ');
  return (
    <>
      <Stack direction="row" spacing={0.75} justifyContent="center" divider={<Typography variant="body2" color="text.disabled">/</Typography>}>
        <Count n={n('pass')} color="success.main" title="AI: Pass" />
        <Count n={n('look')} color="warning.main" title="AI: Needs a look" />
        <Count n={n('fail')} color="error.main" title="AI: Fail" />
      </Stack>
      {extra ? <Typography variant="caption" color="text.secondary" display="block">{extra}</Typography> : null}
    </>
  );
};

const JobsTab = ({ config, health, onOpenPlan, onOpenReview, notify }) => {
  const speeds = useMemo(() => speedsFromConfig(config, health), [config, health]);
  const [remainingSpeed, setRemainingSpeed] = useState('fast');
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(0);
  const [cancelJob, setCancelJob] = useState(null);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [remaining, setRemaining] = useState(null); // { job, readyCount, estUsd }
  const [remainingBusy, setRemainingBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = seq.current + 1;
    seq.current = my;
    setLoading(true);
    try {
      const res = await rrsApi.listJobs({ status, mine: mine ? 'true' : '', page: page + 1, pageSize: PAGE_SIZE });
      if (my === seq.current) { setData({ total: res.total || 0, items: res.items || [] }); setError(''); }
    } catch (e) {
      if (my === seq.current) setError(errText(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [status, mine, page]);

  useEffect(() => { load(); }, [load]);
  usePolling(load, 10000, true);
  // The AI check column only with a backend that sends it (SPEC_AICHECK 4.4).
  const aiCol = data.items.some((j) => j.aiCounts);
  const anyAtGoogle = data.items.some((j) => j.batchSummary && (j.batchSummary.atGoogle || j.batchSummary.active));
  const now = useNow(anyAtGoogle, 30000);

  // "Generate remaining": read the live estimate first, so the confirm shows the
  // real count and the backend's stale-plan check (409 plan_changed) passes.
  const askRemaining = async (job) => {
    setRowBusy(job.jobId);
    try {
      const detail = await rrsApi.getJob(job.jobId);
      const est = detail.estimate || {};
      if (!detail.canStart) {
        notify(`Nothing to generate for job #${job.jobId} right now.`, 'info');
        await load();
        return;
      }
      setRemaining({ job, detail, readyCount: est.readyCount || 0, estUsd: est.estUsd || 0 });
      setRemainingSpeed(defaultSpeed(detail.speed || job.speed, speeds));
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setRowBusy(null);
    }
  };

  // The total at the chosen speed (the backend's stale-plan check recomputes the same number).
  const remainingRate = remaining ? rateFor(remainingSpeed, speeds, remaining.detail) : 0;
  const remainingUsd = remaining
    ? (isBatch(remainingSpeed) ? estimateFor(remaining.readyCount, remainingRate) : remaining.estUsd)
    : 0;

  const doRemaining = async () => {
    if (!remaining) return;
    setRemainingBusy(true);
    const half = isBatch(remainingSpeed);
    try {
      const res = await rrsApi.startJob(remaining.job.jobId, {
        expectedReadyCount: remaining.readyCount, expectedUsd: remainingUsd, speed: remainingSpeed,
      });
      signalSpend();
      notify(`Job #${remaining.job.jobId}: queued ${fmtInt(res.queued)} image(s)${half ? ' at half price' : ''} for about ${fmtUsd(res.estUsd)}.${half ? ' Google usually returns them within hours, at most 24 h.' : ''}`, 'success');
      setRemaining(null);
      onOpenReview(remaining.job.jobId);
    } catch (e) {
      const code = e.status === 409 && e.body ? e.body.code : null;
      if (code === 'plan_changed') {
        const cur = e.body.current || {};
        const n = cur.readyCount !== undefined ? cur.readyCount : remaining.readyCount;
        notify(`The plan changed: now ${fmtInt(n)} image(s), ${fmtUsd(half ? estimateFor(n, remainingRate) : cur.estUsd)} at ${speedLabel(remainingSpeed)}. Check it and try again.`, 'warning');
      } else if (code === 'batch_unavailable') {
        notify(`${batchUnavailableFromError(e)} Nothing was queued.`, 'warning');
      } else if (code === 'old_plan') {
        // A plan made before the current rules (spec 4.11): decided on the plan (re-plan or start anyway).
        notify(`${serverText(e)} Open the plan to decide.`, 'warning');
      } else if (isDailyLimit(e)) {
        // Never retried automatically: the server's words, then refresh the budget.
        notify(dailyLimitText(e), 'error');
        signalSpend();
      } else {
        notify(errText(e), 'error');
      }
      setRemaining(null);
      await load();
    } finally {
      setRemainingBusy(false);
    }
  };

  const doCancel = async () => {
    if (!cancelJob) return;
    setCancelBusy(true);
    try {
      const res = await rrsApi.cancelJob(cancelJob.jobId, cancelNote.trim());
      notify(`Job #${cancelJob.jobId} cancelled: ${fmtInt(res.cancelledGenerations)} queued generation(s) cancelled.${res.batchesCancelling ? ` ${fmtInt(res.batchesCancelling)} half-price batch(es) at Google are being cancelled; images already made are still collected.` : ''}`, 'info');
      setCancelJob(null);
      setCancelNote('');
      await load();
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setCancelBusy(false);
    }
  };

  const doExport = async (job) => {
    setRowBusy(job.jobId);
    try { await rrsApi.exportJob(job.jobId); } catch (e) { notify(errText(e), 'error'); } finally { setRowBusy(null); }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <TextField
          select size="small" label="Status" value={status} sx={{ minWidth: 180 }}
          onChange={(e) => { setPage(0); setStatus(e.target.value); }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(JOB_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={mine} onChange={(e) => { setPage(0); setMine(e.target.checked); }} />} label="Mine only" />
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={20} />}
        <Button startIcon={<MdRefresh />} onClick={load}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        {/* Review fix RP-6: 10 px cell sides (16 px at the table's edges) so the 12 columns with the AI check still fit
            a 1440 px laptop screen without a sideways scroll to reach Actions. */}
        <Table
          size="small"
          sx={{
            '& .MuiTableCell-root': { px: 1.25 },
            '& .MuiTableCell-root:first-of-type': { pl: 2 },
            '& .MuiTableCell-root:last-of-type': { pr: 2 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Created (NY)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Inputs</TableCell>
              <TableCell align="center">Ready / in progress / done / failed / unknown</TableCell>
              <TableCell align="center">Approved / rejected</TableCell>
              {aiCol && (
                <TableCell align="center">
                  <Tooltip title="AI check of each job's current images: Pass / Needs a look / Fail">
                    <span>AI check</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell align="right">Estimate</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={aiCol ? 12 : 11} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  {loading ? 'Loading...' : 'No jobs yet. Create one in the New job tab.'}
                </TableCell>
              </TableRow>
            )}
            {data.items.map((j) => {
              const g = j.genCounts || {};
              const r = j.reviewCounts || {};
              const atGoogleGens = (g.batched || 0) + (g.submitted || 0);
              const inProgress = (g.queued || 0) + (g.running || 0) + atGoogleGens;
              const bs = j.batchSummary || null;
              const sentMs = bs ? tsMs(bs.oldestSubmittedAt) : null;
              const batchOverdue = sentMs && now - sentMs > OVERDUE_MS;
              const totalGen = inProgress + (g.succeeded || 0) + (g.failed || 0) + (g.unknown || 0);
              const done = (g.succeeded || 0) + (g.failed || 0) + (g.unknown || 0);
              const busy = rowBusy === j.jobId;
              const canRemaining = (j.readyCount || 0) > 0 && ['running', 'completed'].includes(j.status);
              const canCancel = !['completed', 'cancelled'].includes(j.status);
              return (
                <TableRow key={j.jobId} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{j.jobId}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="body2" noWrap title={j.name}>{j.name}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap title={j.createdByEmail}>{j.createdByEmail}</Typography></TableCell>
                  <TableCell><NyTime ts={j.createdAt} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <StatusChip map={JOB_STATUS} status={j.status} />
                      {j.speed && (j.startedAt || totalGen > 0) ? <SpeedChip speed={j.speed} /> : null}
                    </Stack>
                    {bs && (bs.active > 0 || atGoogleGens > 0) ? (
                      <Typography variant="caption" display="block" color={batchOverdue ? 'warning.main' : 'text.secondary'} sx={{ mt: 0.25 }}>
                        {fmtInt(bs.atGoogle || bs.active || 0)} batch{(bs.atGoogle || bs.active) === 1 ? '' : 'es'} at Google
                        {sentMs ? ` · sent ${fmtAgo(bs.oldestSubmittedAt, now)}` : ''}
                        {totalGen ? ` · ${fmtInt(totalGen - inProgress)} of ${fmtInt(totalGen)} back` : ''}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {fmtInt(j.inputCount)}
                    {j.unresolvedCount ? (
                      <Tooltip title="IDs the lookups could not resolve">
                        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>({j.unresolvedCount} unresolved)</Typography>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    <Stack direction="row" spacing={0.75} justifyContent="center" divider={<Typography variant="body2" color="text.disabled">/</Typography>}>
                      <Count n={j.readyCount} color="success.main" title="Ready to generate (not started)" />
                      <Count n={inProgress} color="info.main" title={`${g.queued || 0} queued, ${g.running || 0} generating${atGoogleGens ? `, ${atGoogleGens} at Google (half price)` : ''}`} />
                      <Count n={g.succeeded} color="text.primary" title="Generated" />
                      <Count n={g.failed} color="error.main" title="Failed (no image)" />
                      <Count n={g.unknown} color="warning.main" title="Unknown outcome (timed out after sending, or a half-price result Google never returned): may have been billed" />
                    </Stack>
                    {totalGen > 0 && inProgress > 0 && (
                      <LinearProgress
                        variant="determinate" value={(done / totalGen) * 100}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.75} justifyContent="center" divider={<Typography variant="body2" color="text.disabled">/</Typography>}>
                      <Count n={r.approved} color="success.main" title="Approved" />
                      <Count n={r.rejected} color="error.main" title="Rejected, all attempts" />
                    </Stack>
                    {r.pending ? <Typography variant="caption" color="text.secondary">{fmtInt(r.pending)} to review</Typography> : null}
                  </TableCell>
                  {aiCol && (
                    <TableCell align="center" sx={{ minWidth: 88 }}>
                      <AiCounts c={j.aiCounts} />
                    </TableCell>
                  )}
                  <TableCell align="right"><CostText usd={j.estUsd} /></TableCell>
                  <TableCell align="right">
                    <CostText usd={j.actualUsd} possible={j.possibleUsd} />
                    {Number(j.aiCheckUsd) > 0 ? (
                      // review fix RP-6: wraps under the amount (a nowrap line widened the column and pushed Actions off-screen)
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
                        incl. {fmtUsd(j.aiCheckUsd, Number(j.aiCheckUsd) < 1 ? 3 : 2)} AI checks
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {busy ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    <Tooltip title="Open plan"><IconButton size="small" onClick={() => onOpenPlan(j.jobId)}><MdListAlt /></IconButton></Tooltip>
                    <Tooltip title="Review"><span><IconButton size="small" disabled={!j.startedAt && !totalGen} onClick={() => onOpenReview(j.jobId)}><MdRateReview /></IconButton></span></Tooltip>
                    <Tooltip title={canRemaining ? `Generate remaining (${j.readyCount} ready)` : 'Generate remaining (nothing ready)'}>
                      <span><IconButton size="small" disabled={!canRemaining || busy} onClick={() => askRemaining(j)} sx={{ color: canRemaining ? AI_COLOR : undefined }}><MdPlayArrow /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Export CSV"><span><IconButton size="small" disabled={busy || j.status === 'planning'} onClick={() => doExport(j)}><MdFileDownload /></IconButton></span></Tooltip>
                    <Tooltip title="Cancel job"><span><IconButton size="small" color="error" disabled={!canCancel} onClick={() => { setCancelNote(''); setCancelJob(j); }}><MdCancel /></IconButton></span></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
        rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]}
      />

      <ConfirmDialog
        open={!!remaining} busy={remainingBusy} onClose={() => setRemaining(null)} onConfirm={doRemaining}
        title={remaining ? `Generate remaining for job #${remaining.job.jobId}` : ''}
        confirmLabel={remaining ? `Generate ${fmtInt(remaining.readyCount)}${isBatch(remainingSpeed) ? ' at half price' : ' now'}` : 'Generate'}
      >
        {remaining && (
          <>
            <Box sx={{ mb: 1.5 }}>
              <SpeedChooser
                value={remainingSpeed} onChange={setRemainingSpeed} speeds={speeds} count={remaining.readyCount}
                job={remaining.detail} disabled={remainingBusy}
              />
            </Box>
            {overBudget(config, remainingUsd) && <Alert severity="warning" sx={{ mb: 1.5 }}>{overBudgetText(config, remainingUsd)}</Alert>}
            {isBatch(remainingSpeed) ? (
              <Typography>
                Generate <strong>{fmtInt(remaining.readyCount)}</strong> image{remaining.readyCount === 1 ? '' : 's'} at half price for about <strong>{fmtUsd(remainingUsd)}</strong>?
                Google usually returns them within hours, at most 24 h. You can close this page.
              </Typography>
            ) : (
              <Typography>
                Generate <strong>{fmtInt(remaining.readyCount)}</strong> image{remaining.readyCount === 1 ? '' : 's'} now for about <strong>{fmtUsd(remainingUsd)}</strong>? Results appear as they finish.
              </Typography>
            )}
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!cancelJob} busy={cancelBusy} onClose={() => setCancelJob(null)} onConfirm={doCancel}
        title={cancelJob ? `Cancel job #${cancelJob.jobId}?` : ''} confirmLabel="Cancel job" confirmColor="error"
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Queued generations are cancelled. Images already generating finish and are kept. Half-price batches already at
          Google are cancelled there on a best-effort basis; anything Google already made is still collected and kept.
          Targets that were never generated are released so another job can plan them.
        </Typography>
        <TextField size="small" fullWidth label="Note (optional)" value={cancelNote} onChange={(e) => setCancelNote(e.target.value.slice(0, 500))} />
      </ConfirmDialog>
    </Box>
  );
};

export default JobsTab;
